import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { GitCompare } from 'lucide-react';
import DiffModal from './DiffModal';
import { useAuth } from '../../contexts/AuthContext';
import { prettyJson } from '../../utils/prettyJson';
import './UapiConsole.css';

// The gateway trace, rendered: one block per step, with the decisions it recorded, the bodies it
// carried, and a diff of the two.
//
// SHARED BECAUSE THERE ARE TWO PLACES THAT SHOW A TRACE, and they were diverging. The test console had
// all of this; the Logs tab had a four-column table — step, when, outcome, detail — and therefore no
// redaction set, no grant, no integration version, no bodies and no diff. Same documents, two
// renderers, one of them much poorer, which is the shape of problem the reusable-components rule
// exists to stop. There is now one.
//
// The caller supplies the steps and nothing else: where they came from (a header's transaction id, or a
// query by property) is the caller's business, and this renders whatever it is handed.

// What the two bodies ARE, per step — and the two directions are NOT the same thing.
//
// INBOUND (the `redaction` step, on POST/PUT/PATCH/DELETE) is not redaction of values at all. The
// dispatcher states it plainly: "ON THE WAY IN the rules mean 'may not SET', not 'may not see'." A NODE
// rule PRUNES the field, because a field the caller was never shown cannot meaningfully have been set
// by them; a TEXT rule REJECTS the whole request with 422, because forwarding either their value or the
// asterisks they saw would overwrite good data at the backend. So inbound is a limit on the STRUCTURE a
// caller may send us, and calling it "before/after redaction" describes the wrong operation.
//
// OUTBOUND (the `responded` step) is the redaction proper: the backend's answer, and what this caller
// is permitted to see of it.
//
// `backend` carries a single body that has ALREADY been pruned — labelling that one "before" anything
// would be exactly wrong.
export const BODY_LABELS = {
    redaction: { before: 'as sent by the caller', after: 'as forwarded — fields pruned' },
    responded: { before: 'from the backend', after: 'after redaction' },
    backend: { before: 'sent to the backend', after: 'after redaction' },
};
const DEFAULT_LABELS = { before: 'before', after: 'after' };
export const labelsFor = (step) => BODY_LABELS[step] || DEFAULT_LABELS;

// The same distinction, spelled out on the diff.
export const DIFF_TITLE = {
    redaction: 'inbound — what was pruned from the request',
    responded: 'outbound — what redaction removed from the response',
    backend: 'what was sent to the backend',
};
export const DIFF_SUBTITLE = {
    redaction: 'Inbound rules limit what a caller may SET, not what they may see: a field they were '
        + 'never shown is pruned, and a redacted field carrying a value rejects the request with 422.',
    responded: "The backend's answer, and what this caller is permitted to see of it.",
    backend: 'What was sent to the backend.',
};

// A trace body, indented so a line diff has lines to compare. The trace stores `JSON.stringify(body)`
// with no spacing — one line — and DiffModal states plainly that diffing two single-line documents
// produces one enormous +/- pair that says nothing about what actually differs.
//
// THE IMPLEMENTATION MOVED to utils/prettyJson once a second screen needed it. Re-exported under its
// old name so the callers that import `prettyBody` from here keep working — the name is about a trace
// body, the behaviour is not.
export const prettyBody = prettyJson;

// WHAT WAS GRANTED, IN THE WORDS SOMEBODY WOULD USE.
//
// This read "credential / ALL", which states the level and the mode and never the thing the reader is
// actually asking: WHICH MODULE. A grant lives on a module root, so the answer is "/crm (all)" — the
// module, then how much of it the grant covers, then where the grant came from.
//
//   /crm (all) on the credential              every call under /crm, granted on this credential
//   /rsv (only named calls) on the credential a SELECTED grant — the rest of /rsv stays closed
//   /crm (all) from profile "Vendors"         granted by a profile, not written on this credential
//
// `grant_module` arrives with the dispatcher change that records it. A trace written before that has
// the level and mode only, and still renders — without inventing a module it never recorded.
const GRANT_SOURCE = {
    credential: 'on the credential',
    access: 'on this property binding',
    profile: 'from a profile',
};

export const grantText = (s) => {
    const mode = String(s.grant_mode || '').toUpperCase() === 'SELECTED'
        ? 'only named calls'
        : 'all';
    const source = s.grant_level === 'profile' && s.grant_profile
        ? `from profile "${s.grant_profile}"`
        : GRANT_SOURCE[s.grant_level] || s.grant_level;
    return s.grant_module ? `${s.grant_module} (${mode}) ${source}` : `${mode} ${source}`;
};

// WHERE A NAMED REDACTION SET LIVES. The sets belong to the payload they redact, and the screen is
// addressed by payload with the set chosen inside it.
//
// BY ID, NOT BY NAME. Set names are not unique across payloads — "hotelid" is exactly the kind of name
// two payloads both carry — so addressing this by name could open another payload's set and be
// believed. Older traces carry neither id and get no link rather than a guessed one.
const redactionHref = (s) => (s.redaction_payload_id && s.redaction_set_id
    ? `/admin/universal-api/payloads/${s.redaction_payload_id}/redactions?set=${s.redaction_set_id}`
    : null);

const UapiTraceSteps = ({ steps }) => {
    // Which step's entry/exit pair is open, or null. Held by INDEX rather than by a copy of the bodies,
    // so a re-read of the trace cannot leave a stale document on screen.
    const [diffStep, setDiffStep] = useState(null);
    // THE RULES ARE A SUPERADMIN SCREEN. /admin/universal-api/payloads/:id/redactions is behind
    // requireSuperAdmin, so linking there for anybody else offers a door that shuts in their face —
    // worse than no link, because it reads as a fault. They still see which set applied and what it
    // struck; only the way through is withheld.
    const { isSuperAdmin } = useAuth();

    const list = Array.isArray(steps) ? steps : [];
    if (!list.length) return null;

    return (
        <>
            {list.map((s, i) => (
                <div key={i} className={`uc-step uc-step-${s.outcome || 'ok'}`}>
                    <div className="uc-step-head">
                        <span className="uc-step-name">{s.step}</span>
                        <span className="lu-muted">{s.lambda}</span>
                        {s.status && <span className={`uc-status uc-status-${String(s.status)[0]}xx`}>{s.status}</span>}
                        {s.duration_ms != null && <span className="lu-muted">{s.duration_ms} ms</span>}
                        <span className="uc-step-time">{String(s['@timestamp'] || '').slice(11, 23)}</span>
                    </div>
                    {s.message && <p className="uc-step-msg">{s.message}</p>}

                    {/* The decisions, named. Only what this step actually recorded. */}
                    <div className="uc-step-facts">
                        {s.operation_path && <span><em>operation</em> {s.operation_path}</span>}
                        {s.grant_level && (
                            <span>
                                <em>grant</em> {grantText(s)}
                            </span>
                        )}
                        {s.redaction_set && (
                            <span>
                                <em>redaction</em>{' '}
                                {isSuperAdmin && redactionHref(s)
                                    ? (
                                        <Link className="uc-step-link sa-only" to={redactionHref(s)}
                                            title={`Open the rules in "${s.redaction_set}"`}>
                                            {s.redaction_set}
                                        </Link>
                                    )
                                    : s.redaction_set}
                                {' '}(
                                {/* 'grant' is not an audience level like the others — it means the
                                    operator named this set on this call of this grant, which outranks
                                    every scope. Spelled out so a trace does not read as if an audience
                                    rule matched. */}
                                {s.redaction_match === 'grant'
                                    ? 'chosen on the grant for this call'
                                    : `matched ${s.redaction_match}`}
                                , {s.rule_count} rule(s))
                            </span>
                        )}
                        {/* WHAT THE REDACTION DID. The set and rule count above say what was CHOSEN;
                            these say what happened, which is the question a reader actually has when a
                            field they expected to be gone is still on screen. Only on a step that
                            recorded it — older traces have neither field and must not render "0". */}
                        {s.redacted_count != null && s.redaction_set && (
                            s.redacted_count > 0 ? (
                                <span className="uc-step-redacted">
                                    <em>redacted</em> {s.redacted_count} field(s)
                                    {s.redacted_fields && s.redacted_fields.length > 0
                                        && `: ${[].concat(s.redacted_fields).join(', ')}`}
                                </span>
                            ) : (
                                <span className="uc-step-violation">
                                    <em>redacted nothing</em>
                                    {s.redactable === false
                                        ? ' — the response is not a JSON document, so path rules had nothing to address'
                                        : s.redaction_paths && s.redaction_paths.length > 0
                                            ? ` — no field matched ${[].concat(s.redaction_paths).join(', ')}`
                                            : ' — no rule matched a field in this response'}
                                </span>
                            )
                        )}
                        {s.system_name && <span><em>backend</em> {s.system_name} as {s.property_code || '?'}</span>}
                        {/* Which code answered. A candidate is pinned to one x-hotelid precisely so it
                            can be exercised before release, and reading "it works" off a run that
                            silently used the live version is the mistake this line exists to prevent. */}
                        {s.integration_version != null && (
                            <span><em>integration</em> v{s.integration_version} ({s.integration_source})</span>
                        )}
                        {s.path_params && s.path_params !== '{}' && (
                            <span><em>params</em> {s.path_params}</span>
                        )}
                        {s.pruned && s.pruned.length > 0 && (
                            <span className="uc-step-pruned"><em>pruned</em> {[].concat(s.pruned).join(', ')}</span>
                        )}
                        {s.violations && s.violations.length > 0 && (
                            <span className="uc-step-violation"><em>rejected</em> {[].concat(s.violations).join(', ')}</span>
                        )}
                    </div>

                    {(s.body_before || s.body_after) && (
                        <>
                            {/* Only when there are TWO documents to compare. A step with one body has
                                nothing to diff, and a disabled button invites the reader to work out
                                why. On its own row rather than in a column: it acts on both. */}
                            {s.body_before && s.body_after && (
                                <div className="uc-step-bodies-head">
                                    <button
                                        type="button"
                                        className="uc-diff-btn"
                                        onClick={() => setDiffStep(i)}
                                        title="Compare entry and exit — what this step changed"
                                    >
                                        <GitCompare size={12} /> Diff
                                    </button>
                                </div>
                            )}
                            <div className="uc-step-bodies">
                                {s.body_before && (
                                    <div>
                                        <span className="uc-step-label">{labelsFor(s.step).before}</span>
                                        <pre>{s.body_before}</pre>
                                    </div>
                                )}
                                {s.body_after && (
                                    <div>
                                        <span className="uc-step-label">{labelsFor(s.step).after}</span>
                                        <pre>{s.body_after}</pre>
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </div>
            ))}

            {diffStep !== null && list[diffStep] && (
                <DiffModal
                    title={`${list[diffStep].step} — ${DIFF_TITLE[list[diffStep].step] || 'what this step changed'}`}
                    subtitle={DIFF_SUBTITLE[list[diffStep].step] || 'What this step changed'}
                    leftLabel={labelsFor(list[diffStep].step).before}
                    rightLabel={labelsFor(list[diffStep].step).after}
                    left={prettyBody(list[diffStep].body_before)}
                    right={prettyBody(list[diffStep].body_after)}
                    onClose={() => setDiffStep(null)}
                />
            )}
        </>
    );
};

export default UapiTraceSteps;
